import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./api/client.js";
import type { EventView, ParticipantView, ScoreView } from "./api/types.js";
import { pickBackground } from "./lib/background.js";
import { clearJoinPath, parseJoinPath } from "./lib/joinUrl.js";
import { Backdrop } from "./components/Backdrop.js";
import { NameScreen } from "./screens/NameScreen.js";
import { PlayScreen } from "./screens/PlayScreen.js";
import { BlockedScreen, LoadingScreen } from "./screens/SimpleScreens.js";

/**
 * 画面の大枠。
 *
 * 受付待機・ゲーム・一時停止・結果の切り替えは PlayScreen が開催段階に応じて行う。
 * ここで取り外してしまうと WebSocket が切れ、再開の配信を受け取れなくなるため、
 * 参加できたあとは常に "playing" のまま PlayScreen を保つ。
 */
type Phase = "loading" | "blocked" | "need_name" | "playing";

interface SessionState {
  event: EventView;
  participant: ParticipantView;
  score: ScoreView;
  /** 背景画像の通し番号。サーバーが返さなかった場合は null（背景なし） */
  backgroundSlot: number | null;
}

/**
 * セッションが切れたときの案内。
 *
 * 参加者が手元のQRを読み直せば、その場で新しいセッションが発行されて戻れる。
 * トークンの再発行・参加枠の無効化で戻れない場合もあるため、
 * 読み直しても駄目なら運営を呼ぶ、という二段構えにしている。
 */
const SESSION_LOST_MESSAGE =
  "もう一度QRコードを読み取ってください。読み取っても戻れない場合は、運営スタッフにお声がけください。";

const EMPTY_SCORE: ScoreView = {
  rawScore: 0,
  displayScore: 0,
  solvedCount: 0,
  hintCount: 0,
  wrongCount: 0,
  lastSolvedAt: null,
};

export function App(): React.ReactElement {
  const [phase, setPhase] = useState<Phase>("loading");
  const [session, setSession] = useState<SessionState | null>(null);
  const [blockedMessage, setBlockedMessage] = useState("");
  const [nameSubmitting, setNameSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  // 参加受付が混み合っている間、待っている理由を画面に出す
  const [joinBusy, setJoinBusy] = useState(false);

  /**
   * 起動時の処理。
   *   QRから来た場合  … パスのシークレットとトークンで参加する
   *   Cookie がある場合 … セッションを復元する
   */
  const bootstrap = useCallback(async (): Promise<void> => {
    setPhase("loading");
    setJoinBusy(false);
    const credentials =
      typeof window === "undefined" ? null : parseJoinPath(window.location.pathname);

    // 失敗したときに、QRの照合で弾かれたのか、
    // 手元のセッションが切れただけなのかを区別するために持つ。
    let stage: "join" | "restore" = credentials === null ? "restore" : "join";

    try {
      if (credentials !== null) {
        const joined = await api.join(credentials.secret, credentials.token, () =>
          setJoinBusy(true)
        );
        setJoinBusy(false);
        stage = "restore";
        // 照合できたらURLからシークレットとトークンを消す。
        // 履歴やスクリーンショットに残さないため。
        clearJoinPath();

        if (joined.needsDisplayName) {
          setSession({
            event: joined.event,
            participant: joined.participant,
            score: EMPTY_SCORE,
            backgroundSlot: joined.backgroundSlot ?? null,
          });
          setPhase("need_name");
          return;
        }
      }

      const me = await api.me();
      setSession({
        event: me.event,
        participant: me.participant,
        score: me.score,
        backgroundSlot: me.backgroundSlot ?? null,
      });

      setPhase(me.participant.claimed ? "playing" : "need_name");
    } catch (err) {
      if (err instanceof ApiError) {
        // セッションの復元に失敗しただけなら、手元のQRを読み直せば戻れる。
        // サーバーは失敗理由を区別しないため「無効なQRコード」と返してくるが、
        // それをそのまま出すと、直せる状態なのに参加者は紙が壊れたと受け取り、
        // 自力で戻れるのに運営を呼ぶことになる。
        setBlockedMessage(
          stage === "restore" && err.isUnauthorized ? SESSION_LOST_MESSAGE : err.message
        );
      } else {
        setBlockedMessage("読み込みに失敗しました。もう一度お試しください。");
      }
      setPhase("blocked");
    } finally {
      setJoinBusy(false);
    }
  }, []);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  const handleSubmitName = useCallback(
    async (displayName: string): Promise<void> => {
      setNameSubmitting(true);
      setNameError(null);
      try {
        const res = await api.setDisplayName(displayName);
        const me = await api.me();
        setSession({
          event: me.event,
          participant: res.participant,
          score: me.score,
          backgroundSlot: me.backgroundSlot ?? null,
        });
        setPhase("playing");
      } catch (err) {
        if (err instanceof ApiError && err.isUnauthorized) {
          // 名前を入れている最中に切れた場合も、読み直せば同じ画面に戻る
          setBlockedMessage(SESSION_LOST_MESSAGE);
          setPhase("blocked");
          return;
        }
        setNameError(
          err instanceof ApiError ? err.message : "登録に失敗しました。もう一度お試しください。"
        );
      } finally {
        setNameSubmitting(false);
      }
    },
    []
  );

  /**
   * ゲーム中にセッションが無効になったとき。
   *
   * 有効期限切れ・トークン再発行・参加枠の無効化のいずれでも起こる。
   * 参加者からは区別がつかないので、まず読み直してもらう案内を出す。
   */
  const handleUnauthorized = useCallback((): void => {
    setBlockedMessage(SESSION_LOST_MESSAGE);
    setPhase("blocked");
  }, []);

  if (phase === "loading") return <LoadingScreen busy={joinBusy} />;

  if (phase === "blocked") {
    return <BlockedScreen message={blockedMessage} onRetry={() => void bootstrap()} />;
  }

  if (session === null) {
    return <BlockedScreen message="読み込みに失敗しました。" onRetry={() => void bootstrap()} />;
  }

  /*
   * 背景は参加できた時点（表示名の登録画面）から出す。
   * 番号とイベントIDだけで決まるため、ゲーム中に再読み込みしても変わらない。
   */
  const backdrop = (
    <Backdrop url={pickBackground(session.event.id, session.backgroundSlot)} />
  );

  if (phase === "need_name") {
    return (
      <>
        {backdrop}
        <NameScreen
          event={session.event}
          participant={session.participant}
          submitting={nameSubmitting}
          error={nameError}
          onSubmit={(name) => void handleSubmitName(name)}
        />
      </>
    );
  }

  return (
    <>
      {backdrop}
      <PlayScreen
        event={session.event}
        participant={session.participant}
        initialScore={session.score}
        onUnauthorized={handleUnauthorized}
      />
    </>
  );
}
